/**
 * Calls feature slice (master prompt §6/§7/§13).
 *
 * Four hotel-scoped, provider-independent pages:
 *   - {@link CallsPage}        call history table
 *   - {@link LiveCallsPage}    active calls + live transcript panel + handoff  (re-exported from ./live-calls-page)
 *   - {@link CallDetailPage}   transcript + summary + tool timeline + recording
 *   - {@link ConversationsPage} cross-channel conversation list (placeholder)
 *
 * Every page renders ONLY canonical Hostesa call data from `@hostesa/api-client`
 * hooks. No Vapi/LiveKit specifics, no direct fetches, scoped query keys via the
 * resolved UUIDs from `useHotelContext`. Loading / empty / error / 404 states
 * are all handled; the post-call summary tolerates a 404 ("not ready yet").
 */

import {
  ApiError,
  type Call,
  type CallsFilters,
  useCall,
  useCallSummary,
  useCalls,
  useCallToolCalls,
  useCallTranscript,
} from '@hostesa/api-client';
import { useFormatters, useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
  EmptyState,
  ErrorState,
  LanguagePill,
  ListToolbar,
  LoadingState,
  PageHeader,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@hostesa/ui';
import {
  ChevronRight,
  MessagesSquare,
  PhoneCall,
  PhoneIncoming,
  PhoneOutgoing,
} from 'lucide-react';
import { useHotelContext } from '../../lib/hotel-context';
import {
  durationSeconds,
  humanizeToolName,
  languageLabel,
  optionalText,
  shortId,
} from './components/call-presentation';
import { CallStatusBadge } from './components/call-status-badge';
import { CallRoomSubscription, HotelRoomSubscription } from './components/realtime-subscriptions';
import { type ToolCallItem, ToolCallTimeline } from './components/tool-call-timeline';
import { TranscriptPanel } from './components/transcript-panel';

// Live calls lives in its own module; re-exported here so the router import is stable.
export { LiveCallsPage, type LiveCallsPageProps } from './live-calls-page';

// The shared app-shell container owns width + padding (spec 45); the page keeps
// only its own content structure.
const PAGE_WRAP = '';

const STATUS_OPTIONS = [
  'created',
  'ringing',
  'answered',
  'ai_active',
  'handoff_requested',
  'human_active',
  'completed',
  'failed',
] as const;

// ───────────────────────────────────────────────────────────────────────────
// Calls history
// ───────────────────────────────────────────────────────────────────────────

export interface CallsPageProps {
  /** Server-side filter/search/pagination state (URL-driven by the route). */
  filters?: CallsFilters;
  /** Apply a new filter set (the route writes it to URL search params). */
  onFiltersChange?: (next: CallsFilters) => void;
  /** Navigate to a call detail view. Wired by the router-level route. */
  onSelectCall?: (callId: string) => void;
}

export function CallsPage({ filters = {}, onFiltersChange, onSelectCall }: CallsPageProps = {}) {
  const { t } = useT('calls');
  const { tenantId, hotelId, timezone } = useHotelContext();
  const { formatDateTime, formatDuration } = useFormatters(timezone);

  const apply = onFiltersChange ?? (() => {});
  const limit = filters.limit ?? 50;
  const offset = filters.offset ?? 0;

  const callsQuery = useCalls(tenantId, hotelId, { ...filters, limit, offset });
  const rows = callsQuery.data?.items ?? [];
  const total = callsQuery.data?.total ?? 0;

  const filterActive = Boolean(
    filters.status ||
      filters.direction ||
      filters.language ||
      filters.q ||
      filters.from ||
      filters.to,
  );
  /** Patch filters and reset to the first page (offset 0). */
  const patch = (next: Partial<CallsFilters>) => apply({ ...filters, ...next, offset: 0 });

  return (
    <section data-testid="calls-page" className={PAGE_WRAP}>
      <HotelRoomSubscription tenantId={tenantId} hotelId={hotelId} />
      <PageHeader
        eyebrow={t('history.eyebrow')}
        title={t('history.title')}
        description={t('history.description')}
      />

      <ListToolbar
        data-testid="calls-toolbar"
        searchValue={filters.q ?? ''}
        onSearchChange={(v) => patch({ q: v || undefined })}
        searchPlaceholder={t('history.toolbar.searchPlaceholder')}
        trailing={
          filterActive ? (
            <Button
              data-testid="calls-clear-filters"
              variant="ghost"
              size="sm"
              onClick={() => apply({ limit })}
            >
              {t('history.toolbar.clear')}
            </Button>
          ) : undefined
        }
      >
        <Select
          value={filters.status ?? 'all'}
          onValueChange={(v) => patch({ status: v === 'all' ? undefined : (v as Call['status']) })}
        >
          <SelectTrigger data-testid="calls-filter-trigger" className="h-8 w-44">
            <SelectValue placeholder={t('history.toolbar.statusAll')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="calls-filter-all">
              {t('history.toolbar.statusAll')}
            </SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s} value={s} data-testid={`calls-filter-${s}`}>
                {t(`status.${s}`)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={filters.direction ?? 'all'}
          onValueChange={(v) =>
            patch({ direction: v === 'all' ? undefined : (v as 'inbound' | 'outbound') })
          }
        >
          <SelectTrigger data-testid="calls-direction-trigger" className="h-8 w-40">
            <SelectValue placeholder={t('history.toolbar.directionAll')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('history.toolbar.directionAll')}</SelectItem>
            <SelectItem value="inbound">{t('direction.inbound')}</SelectItem>
            <SelectItem value="outbound">{t('direction.outbound')}</SelectItem>
          </SelectContent>
        </Select>
        <input
          type="date"
          aria-label={t('history.toolbar.fromLabel')}
          data-testid="calls-from"
          value={filters.from ?? ''}
          onChange={(e) => patch({ from: e.target.value || undefined })}
          className="h-8 rounded-md border border-[var(--color-border)] bg-[var(--color-alabaster)] px-2 text-sm text-[var(--color-charcoal)]"
        />
        <input
          type="date"
          aria-label={t('history.toolbar.toLabel')}
          data-testid="calls-to"
          value={filters.to ?? ''}
          onChange={(e) => patch({ to: e.target.value || undefined })}
          className="h-8 rounded-md border border-[var(--color-border)] bg-[var(--color-alabaster)] px-2 text-sm text-[var(--color-charcoal)]"
        />
      </ListToolbar>

      {callsQuery.isLoading ? (
        <LoadingState data-testid="calls-loading" title={t('history.loading')} />
      ) : callsQuery.isError ? (
        <ErrorState
          data-testid="calls-error"
          title={t('history.error.title')}
          description={t('history.error.description')}
        />
      ) : rows.length === 0 ? (
        filterActive ? (
          <EmptyState
            data-testid="calls-empty-filtered"
            icon={<PhoneCall />}
            title={t('history.emptyFiltered.title')}
            description={t('history.emptyFiltered.description')}
            action={
              <Button
                data-testid="calls-clear-filters-empty"
                variant="secondary"
                size="sm"
                onClick={() => apply({ limit })}
              >
                {t('history.toolbar.clear')}
              </Button>
            }
          />
        ) : (
          <EmptyState
            data-testid="calls-empty"
            icon={<PhoneCall />}
            title={t('history.empty.title')}
            description={t('history.empty.description')}
          />
        )
      ) : (
        <Card className="p-2 sm:p-3">
          <ul data-testid="calls-table" className="flex flex-col">
            {rows.map((call) => {
              const dur = durationSeconds(call.durationSeconds);
              const DirectionIcon = call.direction === 'outbound' ? PhoneOutgoing : PhoneIncoming;
              const lang =
                typeof call.language === 'string' && call.language.trim()
                  ? call.language.trim().toUpperCase()
                  : null;
              return (
                <li key={call.id}>
                  <button
                    type="button"
                    data-testid={`call-row-${call.id}`}
                    onClick={() => onSelectCall?.(call.id)}
                    className={cn(
                      'flex w-full items-center gap-4 border-b border-[var(--color-border)] px-3 py-3.5 text-left last:border-0',
                      'transition-colors hover:bg-[var(--color-canvas)]',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-canvas)] text-[var(--color-stone-600)]"
                    >
                      <DirectionIcon className="size-4" strokeWidth={1.3} />
                    </span>
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <CallStatusBadge status={call.status} />
                      <span className="font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
                        {t(`direction.${call.direction}`)}
                        {' · '}
                        {call.startedAt ? formatDateTime(call.startedAt) : t('common.notAvailable')}
                      </span>
                    </div>
                    {lang && <LanguagePill code={lang} />}
                    <span className="w-14 text-right font-[var(--font-mono)] text-xs text-[var(--color-stone-mid)]">
                      {dur !== null ? formatDuration(dur) : t('common.notAvailable')}
                    </span>
                    <ChevronRight
                      aria-hidden="true"
                      className="size-4 shrink-0 text-[var(--color-stone-300)]"
                    />
                  </button>
                </li>
              );
            })}
          </ul>
        </Card>
      )}

      {!callsQuery.isLoading && !callsQuery.isError && total > 0 && (
        <Pagination
          data-testid="calls-pagination"
          total={total}
          limit={limit}
          offset={offset}
          onChange={({ offset: nextOffset, limit: nextLimit }) =>
            apply({ ...filters, offset: nextOffset, limit: nextLimit })
          }
          rangeLabel={(from, to, t1) => t('history.pagination.range', { from, to, total: t1 })}
          prevLabel={t('history.pagination.prev')}
          nextLabel={t('history.pagination.next')}
          pageSizeLabel={t('history.pagination.perPage')}
        />
      )}
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Call detail
// ───────────────────────────────────────────────────────────────────────────

export interface CallDetailPageProps {
  /** Canonical call UUID from the route param `$callId`. */
  callId: string;
  /** Navigate back to the call history list. */
  onBack?: () => void;
}

export function CallDetailPage({ callId, onBack }: CallDetailPageProps) {
  const { t } = useT('calls');
  const { tenantId, hotelId, timezone } = useHotelContext();
  const { formatDateTime, formatDuration } = useFormatters(timezone);

  const callQuery = useCall(tenantId, hotelId, callId);
  const transcriptQuery = useCallTranscript(tenantId, hotelId, callId);
  const summaryQuery = useCallSummary(tenantId, hotelId, callId);
  const toolCallsQuery = useCallToolCalls(tenantId, hotelId, callId);

  if (callQuery.isLoading) {
    return (
      <section data-testid="call-detail-page" className={PAGE_WRAP}>
        <LoadingState data-testid="call-detail-loading" title={t('detail.loading')} />
      </section>
    );
  }

  if (callQuery.isError) {
    const notFound = callQuery.error instanceof ApiError && callQuery.error.isNotFound;
    return (
      <section data-testid="call-detail-page" className={PAGE_WRAP}>
        <ErrorState
          data-testid={notFound ? 'call-detail-not-found' : 'call-detail-error'}
          title={notFound ? t('detail.notFound.title') : t('detail.error.title')}
          description={notFound ? t('detail.notFound.description') : t('detail.error.description')}
          action={
            onBack ? (
              <Button
                type="button"
                data-testid="call-detail-back-btn"
                variant="ghost"
                onClick={onBack}
              >
                {t('detail.backToCalls')}
              </Button>
            ) : undefined
          }
        />
      </section>
    );
  }

  const call = callQuery.data;
  const dur = durationSeconds(call?.durationSeconds);
  const endReason = optionalText(call?.endReason);

  // A 404 summary means "not generated yet", not a hard error (404-tolerant).
  const summaryNotReady =
    summaryQuery.isError && summaryQuery.error instanceof ApiError && summaryQuery.error.isNotFound;

  // Map the canonical (payload-free) tool-call records to timeline items.
  // Tool names + outcomes go through i18n (defaulting to a humanized name for
  // canonical tools without an explicit label); timestamps use the same
  // hotel-timezone formatter as the other fields on this page.
  const toolItems: ToolCallItem[] = (toolCallsQuery.data ?? []).map((tc) => ({
    id: tc.id,
    name: t(`detail.tools.names.${tc.name}`, { defaultValue: humanizeToolName(tc.name) }),
    decision: t(`detail.tools.outcome.${tc.outcome}`, { defaultValue: tc.outcome }),
    occurredAtLabel: formatDateTime(tc.occurredAt),
  }));

  return (
    <section data-testid="call-detail-page" className={PAGE_WRAP}>
      <CallRoomSubscription tenantId={tenantId} hotelId={hotelId} callId={callId} />
      <PageHeader
        eyebrow={t('detail.eyebrow')}
        title={t('detail.title')}
        actions={
          onBack ? (
            <Button
              type="button"
              data-testid="call-detail-back-btn"
              variant="ghost"
              onClick={onBack}
            >
              {t('detail.backToCalls')}
            </Button>
          ) : undefined
        }
      />

      {call && (
        <dl
          data-testid="call-detail-fields"
          className="mb-6 grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-4"
        >
          <div className="flex flex-col gap-0.5">
            <dt className="text-[var(--color-stone-mid)]">{t('detail.fields.callId')}</dt>
            <dd className="font-[var(--font-mono)]">{shortId(call.id)}</dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-[var(--color-stone-mid)]">{t('detail.fields.status')}</dt>
            <dd>
              <CallStatusBadge status={call.status} data-testid="call-detail-status" />
            </dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-[var(--color-stone-mid)]">{t('detail.fields.language')}</dt>
            <dd>{languageLabel(call.language, t('common.notAvailable'))}</dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-[var(--color-stone-mid)]">{t('detail.fields.duration')}</dt>
            <dd>{dur !== null ? formatDuration(dur) : t('common.notAvailable')}</dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-[var(--color-stone-mid)]">{t('detail.fields.direction')}</dt>
            <dd>{t(`direction.${call.direction}`)}</dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-[var(--color-stone-mid)]">{t('detail.fields.startedAt')}</dt>
            <dd>{call.startedAt ? formatDateTime(call.startedAt) : t('common.notAvailable')}</dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-[var(--color-stone-mid)]">{t('detail.fields.endedAt')}</dt>
            <dd>{call.endedAt ? formatDateTime(call.endedAt) : t('common.notAvailable')}</dd>
          </div>
          <div className="flex flex-col gap-0.5">
            <dt className="text-[var(--color-stone-mid)]">{t('detail.fields.endReason')}</dt>
            <dd>{endReason ?? t('common.notAvailable')}</dd>
          </div>
        </dl>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card data-testid="call-detail-transcript-card">
          <CardHeader>
            <CardTitle>{t('detail.transcript.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {transcriptQuery.isLoading ? (
              <LoadingState data-testid="call-detail-transcript-loading" />
            ) : transcriptQuery.isError ? (
              <ErrorState
                data-testid="call-detail-transcript-error"
                title={t('detail.error.title')}
                description={t('detail.error.description')}
              />
            ) : (
              <TranscriptPanel
                data-testid="call-detail-transcript"
                segments={transcriptQuery.data ?? []}
              />
            )}
          </CardContent>
        </Card>

        <Card data-testid="call-detail-summary-card">
          <CardHeader>
            <CardTitle>{t('detail.summary.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {summaryQuery.isLoading ? (
              <LoadingState data-testid="call-detail-summary-loading" />
            ) : summaryNotReady || summaryQuery.isError ? (
              <EmptyState
                data-testid="call-detail-summary-not-ready"
                title={t('detail.summary.notReady.title')}
                description={t('detail.summary.notReady.description')}
              />
            ) : (
              <p data-testid="call-detail-summary" className="text-sm leading-relaxed">
                {summaryQuery.data?.summary}
              </p>
            )}
          </CardContent>
        </Card>

        <Card data-testid="call-detail-tools-card">
          <CardHeader>
            <CardTitle>{t('detail.tools.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            {toolCallsQuery.isLoading ? (
              <LoadingState data-testid="call-detail-tools-loading" />
            ) : toolCallsQuery.isError ? (
              <ErrorState
                data-testid="call-detail-tools-error"
                title={t('detail.error.title')}
                description={t('detail.error.description')}
              />
            ) : (
              <ToolCallTimeline data-testid="call-detail-tools" items={toolItems} />
            )}
          </CardContent>
        </Card>

        <Card data-testid="call-detail-recording-card">
          <CardHeader>
            <CardTitle>{t('detail.recording.title')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-[var(--color-stone-mid)]">
            {t('detail.recording.placeholder')}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Conversations (cross-channel placeholder)
// ───────────────────────────────────────────────────────────────────────────

export function ConversationsPage() {
  const { t } = useT('calls');
  const { tenantId, hotelId } = useHotelContext();

  // Conversations unify voice (calls) with future chat channels. Until a
  // dedicated conversations endpoint exists, this surfaces a calm EmptyState
  // rather than re-deriving a partial list from calls data.
  const callsQuery = useCalls(tenantId, hotelId, { limit: 50 });
  const hasCalls = (callsQuery.data?.total ?? 0) > 0;

  return (
    <section data-testid="conversations-page" className={PAGE_WRAP}>
      <PageHeader
        eyebrow={t('conversations.eyebrow')}
        title={t('conversations.title')}
        description={t('conversations.description')}
      />
      {callsQuery.isLoading ? (
        <LoadingState data-testid="conversations-loading" title={t('history.loading')} />
      ) : callsQuery.isError ? (
        <ErrorState
          data-testid="conversations-error"
          title={t('history.error.title')}
          description={t('history.error.description')}
        />
      ) : (
        <EmptyState
          data-testid="conversations-empty"
          icon={<MessagesSquare />}
          title={t('conversations.empty.title')}
          description={
            hasCalls ? t('conversations.empty.description') : t('conversations.empty.description')
          }
        />
      )}
    </section>
  );
}
